/**
 * The words for the values the Search & Remediate API sends in English: a run's or a
 * message's status, the remediation action, a mailbox's status, a failure's reason code. They
 * arrive as enum values ('Partially Remediated', 'Quarantine'), so they are shown through these
 * maps and the raw value stays as the last resort (docs/LOCALE.md §3.5). The keys live in
 * messages/{en,ar,hi,fr}.json under er.snr; a Map escapes the static-keys guard, so
 * `snr-console-speaks-the-interface-language.spec.tsx` proves every key here resolves.
 *
 * The verbs the page OFFERS are `SNR_OFFERED_ACTIONS` (owner, 27 Oct 2026, DECISIONS #1): the four that
 * move mail, Trash (can be undone) and Delete (permanent, behind its permissions). `Label` and `Ticket`
 * keep a label here ONLY so a run made before v2 still reads; nothing offers them.
 */
import type { I18nContextValue } from '@/lib/i18n'
import type { ErSnrAction, ErSnrStatus } from '@/lib/er/types'
import type { SnrActionVerb, SnrIssue, SnrJobPause, SnrMissingPermission } from '@/lib/er/snr-query/types'
import { snrIssueKey } from '@/lib/er/snr-refusal'

type Translate = I18nContextValue['t']

/** A run's status, and a found message's (a subset of it). */
export const SNR_STATUS_KEYS: Readonly<Record<ErSnrStatus, string>> = {
  Pending: 'er.snr.status.Pending',
  Searching: 'er.snr.status.Searching',
  Searched: 'er.snr.status.Searched',
  Remediating: 'er.snr.status.Remediating',
  Remediated: 'er.snr.status.Remediated',
  'Partially Remediated': 'er.snr.status.PartiallyRemediated',
  Paused: 'er.snr.status.Paused',
  Cancelled: 'er.snr.status.Cancelled',
  Failed: 'er.snr.status.Failed',
  'Searched With Gaps': 'er.snr.status.SearchedWithGaps',
}

/**
 * v2 run statuses that the legacy list does not have. The run list spells the enum label with
 * spaces, /progress in one word; both read the same sentence.
 */
export const SNR_V2_STATUS_KEYS: Readonly<Record<string, string>> = {
  'Searched With Gaps': 'er.snr.status.SearchedWithGaps',
  SearchedWithGaps: 'er.snr.status.SearchedWithGaps',
}

/** The remediation actions, the four offered and the legacy ones a past run may carry. */
export const SNR_ACTION_KEYS: Readonly<Record<ErSnrAction, string>> = {
  Inbox: 'er.snr.action.Inbox',
  Label: 'er.snr.action.Label',
  Quarantine: 'er.snr.action.Quarantine',
  Trash: 'er.snr.action.Trash',
  Delete: 'er.snr.action.Delete',
  Ticket: 'er.snr.action.Ticket',
  Junk: 'er.snr.action.Junk',
  Move: 'er.snr.action.Move',
}

/** The verbs the page offers, in menu order: the moves, then Trash, then the permanent Delete last. */
export const SNR_OFFERED_ACTIONS: readonly SnrActionVerb[] = ['Quarantine', 'Junk', 'Move', 'Inbox', 'Trash', 'Delete']

/** One line under each verb in the Actions menu: what it does, and whether it can be undone. */
export const SNR_ACTION_HINT_KEYS: Readonly<Record<SnrActionVerb, string>> = {
  Quarantine: 'er.snr.actionHint.Quarantine',
  Junk: 'er.snr.actionHint.Junk',
  Move: 'er.snr.actionHint.Move',
  Inbox: 'er.snr.actionHint.Inbox',
  Trash: 'er.snr.actionHint.Trash',
  Delete: 'er.snr.actionHint.Delete',
}

/**
 * Why Delete can't be done, as the page says it (DisabledWithReason, a refusal toast): the three permissions of the
 * shared Delete gate (er_delete_permission_missing { reason }). `provider_scope` has a sentence that names the domain
 * (`providerScopeDomain`, when one is known) and one that does not.
 */
export const SNR_DELETE_GATE_KEYS: Readonly<Record<'role' | 'switch' | 'provider_scope', string>> = {
  role: 'er.snr.deleteGate.role',
  switch: 'er.snr.deleteGate.switch',
  provider_scope: 'er.snr.deleteGate.providerScope',
}
export const SNR_DELETE_GATE_DOMAIN_KEY = 'er.snr.deleteGate.providerScopeDomain'

/** What a job that paused itself is missing, in a sentence that says what to do about it. */
export const SNR_PAUSE_MISSING_KEYS: Readonly<Record<SnrMissingPermission, string>> = {
  role: 'er.snr.pause.missing.role',
  switch: 'er.snr.pause.missing.switch',
  provider_scope: 'er.snr.pause.missing.provider_scope',
  mail_permission: 'er.snr.pause.missing.mail_permission',
}

/** The sentence of a job's pause: what is missing, with the provider in words. */
export function snrPauseText(t: Translate, pause: Pick<SnrJobPause, 'missing' | 'provider'>): string {
  return t(SNR_PAUSE_MISSING_KEYS[pause.missing] ?? SNR_PAUSE_MISSING_KEYS.mail_permission, {
    provider: pause.provider ? snrProviderLabel(t, pause.provider) : t('er.snr.pause.theProvider'),
  })
}

/** A mailbox's search status. */
export const SNR_MAILBOX_STATUS_KEYS: Readonly<Record<string, string>> = {
  Pending: 'er.snr.mailboxStatus.Pending',
  Searching: 'er.snr.mailboxStatus.Searching',
  Searched: 'er.snr.mailboxStatus.Searched',
  Failed: 'er.snr.mailboxStatus.Failed',
  Skipped: 'er.snr.mailboxStatus.Skipped',
  Capped: 'er.snr.mailboxStatus.Capped',
}

/** A copy's last action, and an action job. */
export const SNR_ACTION_STATUS_KEYS: Readonly<Record<string, string>> = {
  Pending: 'er.snr.actionStatus.Pending',
  Running: 'er.snr.actionStatus.Running',
  Paused: 'er.snr.actionStatus.Paused',
  Succeeded: 'er.snr.actionStatus.Succeeded',
  Failed: 'er.snr.actionStatus.Failed',
  Skipped: 'er.snr.actionStatus.Skipped',
  Done: 'er.snr.actionStatus.Done',
  DoneWithFailures: 'er.snr.actionStatus.DoneWithFailures',
  Cancelled: 'er.snr.actionStatus.Cancelled',
}

export const SNR_JOB_KIND_KEYS: Readonly<Record<string, string>> = {
  action: 'er.snr.jobKind.action',
  retry: 'er.snr.jobKind.retry',
  undo: 'er.snr.jobKind.undo',
}

/** Why a mailbox, or a copy, was not searched or not moved (the pipeline's reason codes). */
export const SNR_REASON_KEYS: Readonly<Record<string, string>> = {
  throttled: 'er.snr.reason.throttled',
  auth_failed: 'er.snr.reason.auth_failed',
  consent_revoked: 'er.snr.reason.consent_revoked',
  mailbox_not_found: 'er.snr.reason.mailbox_not_found',
  not_in_app_scope: 'er.snr.reason.not_in_app_scope',
  suppressed: 'er.snr.reason.suppressed',
  no_licence: 'er.snr.reason.no_licence',
  query_rejected: 'er.snr.reason.query_rejected',
  provider_down: 'er.snr.reason.provider_down',
  capped: 'er.snr.reason.capped',
  not_in_domain: 'er.snr.reason.not_in_domain',
  message_not_found: 'er.snr.reason.message_not_found',
  folder_unavailable: 'er.snr.reason.folder_unavailable',
  cancelled: 'er.snr.reason.cancelled',
  provider_refused: 'er.snr.reason.provider_refused',
  stalled: 'er.snr.reason.stalled',
  paused_expired: 'er.snr.reason.paused_expired',
  // Skipped at dispatch: a search could not search this mailbox in the last 30 minutes (owner, 3 Nov 2026).
  not_available: 'er.snr.reason.not_available',
  // Another job of ours (another search, an action, a crashed job's lease) held the mailbox: never a provider limit.
  mailbox_busy: 'er.snr.reason.mailbox_busy',
}

/**
 * Why ONE mailbox of a finished search was not searched, as the sentence the gaps list shows (owner, 2 Nov 2026:
 * "Not searched: Google is limiting this mailbox right now"). By reason and provider first, then by reason; any other
 * reason reads "Not searched: <its label>".
 */
export const SNR_GAP_REASON_KEYS: Readonly<Record<string, string>> = {
  throttled_google: 'er.snr.gaps.why.throttled_google',
  throttled_microsoft: 'er.snr.gaps.why.throttled_microsoft',
  throttled: 'er.snr.gaps.why.throttled',
  provider_down_google: 'er.snr.gaps.why.provider_down_google',
  provider_down_microsoft: 'er.snr.gaps.why.provider_down_microsoft',
  provider_down: 'er.snr.gaps.why.provider_down',
  stalled: 'er.snr.gaps.why.stalled',
  mailbox_busy: 'er.snr.gaps.why.mailbox_busy',
}

export function snrGapReasonText(t: Translate, reasonCode: string | null | undefined, provider: string | null | undefined): string {
  const code = reasonCode ?? 'provider_down'
  const key = SNR_GAP_REASON_KEYS[`${code}_${provider ?? ''}`] ?? SNR_GAP_REASON_KEYS[code]
  if (key) return t(key)
  return t('er.snr.gaps.why.other', { reason: snrReasonLabel(t, reasonCode, { provider }) })
}

/** A copy's folder. */
export const SNR_FOLDER_KEYS: Readonly<Record<string, string>> = {
  inbox: 'er.snr.folder.inbox',
  junk: 'er.snr.folder.junk',
  deleted: 'er.snr.folder.deleted',
  archive: 'er.snr.folder.archive',
  other: 'er.snr.folder.other',
}

export const SNR_PROVIDER_KEYS: Readonly<Record<string, string>> = {
  microsoft: 'er.snr.provider.microsoft',
  google: 'er.snr.provider.google',
}

/**
 * A compile blocker or warning in words. The server names the provider it is about ("microsoft",
 * "google"); the sentence says it in the interface language ("on Microsoft 365"), never the code.
 */
export function snrIssueText(t: Translate, issue: SnrIssue): string {
  const params: Record<string, string | number> | undefined = issue.params
    ? {
        ...issue.params,
        ...(typeof issue.params.provider === 'string' ? { provider: snrProviderLabel(t, issue.params.provider) } : {}),
      }
    : undefined
  return t(snrIssueKey(issue), params)
}

export function snrStatusLabel(t: Translate, status: string): string {
  const key =
    (SNR_STATUS_KEYS as Readonly<Record<string, string>>)[status] ?? SNR_V2_STATUS_KEYS[status]
  return key ? t(key) : status
}

export function snrActionLabel(t: Translate, action: string): string {
  const key = (SNR_ACTION_KEYS as Readonly<Record<string, string>>)[action]
  return key ? t(key) : action
}

export function snrMailboxStatusLabel(t: Translate, status: string): string {
  const key = SNR_MAILBOX_STATUS_KEYS[status]
  return key ? t(key) : status
}

export function snrActionStatusLabel(t: Translate, status: string): string {
  const key = SNR_ACTION_STATUS_KEYS[status]
  return key ? t(key) : status
}

/**
 * `mailbox_not_found` in its six sentences (spec 2026-10-29-mailbox-gone-design.md). The FACT (the provider's word that the
 * mailbox does not exist), with the provider when the row names it; and the same fact followed by "the person has been set
 * Inactive", which only a screen that holds the person's recipient record with `mailboxGone` set may say: the safeguards can
 * hold a verdict (no domain evidence, a person under 48 hours) and the outcome is not stored on the execution. A Map, so
 * snr-mailbox-gone.spec.tsx proves every key resolves in the four languages.
 */
export const SNR_MAILBOX_GONE_KEYS = {
  generic: 'er.snr.reason.mailbox_not_found',
  google: 'er.snr.reason.mailbox_not_found_google',
  microsoft: 'er.snr.reason.mailbox_not_found_microsoft',
  generic_inactive: 'er.snr.reason.mailbox_not_found_inactive',
  google_inactive: 'er.snr.reason.mailbox_not_found_google_inactive',
  microsoft_inactive: 'er.snr.reason.mailbox_not_found_microsoft_inactive',
} as const

export interface SnrReasonOptions {
  /** The mailbox's provider ("google" | "microsoft"), when the screen has it; another value reads as the generic fact. */
  provider?: string | null
  /** The screen holds the person's recipient record and it carries `mailboxGone`: say they were set Inactive. */
  personInactive?: boolean
}

/**
 * A reason code in words; a code this page does not know reads as the plain "could not be completed". `options` only
 * matters to `mailbox_not_found`, the one reason whose sentence depends on the provider and on what the screen knows.
 */
export function snrReasonLabel(t: Translate, reasonCode: string | null | undefined, options: SnrReasonOptions = {}): string {
  if (reasonCode === 'mailbox_not_found') {
    const where = options.provider === 'google' || options.provider === 'microsoft' ? options.provider : 'generic'
    const key = options.personInactive ? (`${where}_inactive` as keyof typeof SNR_MAILBOX_GONE_KEYS) : where
    return t(SNR_MAILBOX_GONE_KEYS[key])
  }
  const key = reasonCode ? SNR_REASON_KEYS[reasonCode] : undefined
  return t(key ?? 'er.snr.reason.unknown')
}

export function snrFolderLabel(t: Translate, folder: string): string {
  const key = SNR_FOLDER_KEYS[folder]
  return key ? t(key) : folder
}

export function snrProviderLabel(t: Translate, provider: string): string {
  const key = SNR_PROVIDER_KEYS[provider]
  return key ? t(key) : provider
}

/**
 * The names the Columns menu shows for the found-messages table (the column ids are
 * `accessorKey`s, which the menu would otherwise print as they are).
 */
export function snrMessageColumnNames(t: Translate): Record<string, string> {
  return {
    senderEmail: t('er.snr.messages.sender'),
    receiverEmail: t('er.snr.messages.recipient'),
    folderName: t('er.snr.messages.folder'),
    subject: t('er.snr.messages.subject'),
    receivedAt: t('er.snr.messages.received'),
    status: t('er.snr.messages.status'),
    domain: t('er.snr.messages.domain'),
  }
}

/** The same for the v2 messages table. */
export function snrV2MessageColumnNames(t: Translate): Record<string, string> {
  return {
    subject: t('er.snr.messages.subject'),
    senderAddress: t('er.snr.messages.sender'),
    mailbox: t('er.snr.messages.mailbox'),
    provider: t('er.snr.messages.provider'),
    folder: t('er.snr.messages.folder'),
    receivedAt: t('er.snr.messages.received'),
    sizeBytes: t('er.snr.messages.size'),
    attachments: t('er.snr.messages.attachments'),
    lastAction: t('er.snr.messages.lastAction'),
    domain: t('er.snr.messages.domain'),
  }
}

/** The same for the mailboxes table. */
export function snrMailboxColumnNames(t: Translate): Record<string, string> {
  return {
    email: t('er.snr.mailboxes.email'),
    provider: t('er.snr.messages.provider'),
    status: t('er.snr.messages.status'),
    matches: t('er.snr.mailboxes.matches'),
    pagesRead: t('er.snr.mailboxes.pagesRead'),
    reasonCode: t('er.snr.mailboxes.reason'),
    domain: t('er.snr.messages.domain'),
  }
}

/** The same for the run-history table. */
export function snrRunColumnNames(t: Translate): Record<string, string> {
  return {
    status: t('er.snr.logs.status'),
    criteria: t('er.snr.logs.criteria'),
    createdAt: t('er.snr.logs.started'),
    ranBy: t('er.snr.logs.ranBy'),
    mailboxes: t('er.snr.logs.mailboxes'),
    copiesFound: t('er.snr.logs.copiesFound'),
    actionsTaken: t('er.snr.logs.actionsTaken'),
    copiesRemoved: t('er.snr.logs.copiesRemoved'),
    domain: t('er.snr.messages.domain'),
  }
}
